import { UNSUBSCRIBE_FUNCTION } from '../../config/functionRoutes';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { collection, doc, getDocs, query, updateDoc, where, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useEventEdition } from '../../contexts/EventEditionContext';
import { listEmailTemplates, EMAIL_TEMPLATE_LOCALES, EmailTemplate, EmailTemplateLocale } from '../../services/templateService';
import Handlebars from 'handlebars';
import { registerDefaultEmailHelpers } from '../../services/handlebarsHelpers';
import { enqueueRawEmail } from '../../services/emailService';
import { listEventEditions, EventEditionSummary } from '../../services/eventEditionService';
import { resolveBoilerplates } from '../../services/boilerplateResolver';
import { buildEmailContext } from '../../services/emailContextBuilder';
import { kutcStarterIndex, kutcParticipationStatus, KutcParticipationFilter } from '../../utils/kutcStarterFilter';

// Basic statuses available on registrations
const REG_STATUSES = ['pending', 'confirmed', 'cancelled', 'expired'] as const;

type SourceType = 'registrations' | 'users';
type FilterPreset = 'custom' | 'kutc-started-edition' | 'kutc-opted-in-participants' | 'mo-opted-in-participants';
type CampaignType = 'transactional' | 'notifyFutureEvents' | 'sendRunningOffers';

const FUNCTIONS_BASE = `https://europe-west1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net`;
const PRESET_DISABLED = (preset: FilterPreset) => preset !== 'custom';

const BulkEmailPanel: React.FC = () => {
  const { event } = useEventEdition();

  const [loading, setLoading] = useState(false);
  const [campaignType, setCampaignType] = useState<CampaignType>('transactional');

  // Filter preset
  const [filterPreset, setFilterPreset] = useState<FilterPreset>('custom');
  const startedOnly = filterPreset === 'kutc-started-edition';

  // Filters
  const [source, setSource] = useState<SourceType>('registrations');
  const [participation, setParticipation] = useState<KutcParticipationFilter>('any');
  const effectiveParticipation = startedOnly ? 'started' : filterPreset === 'custom' && source === 'registrations' ? participation : 'any';
  const needsRaceResults = effectiveParticipation !== 'any';
  const [editionId, setEditionId] = useState<string>('');
  const [templateEditionOverride, setTemplateEditionOverride] = useState<string>('');
  const [statuses, setStatuses] = useState<string[]>(['confirmed']);
  const [excludeCancelled, setExcludeCancelled] = useState(true);
  const [flagWaitinglist, setFlagWaitinglist] = useState<boolean | 'any'>('any');
  const [flagNotifyFuture, setFlagNotifyFuture] = useState<boolean | 'any'>('any');
  const [flagSendOffers, setFlagSendOffers] = useState<boolean | 'any'>('any');

  // Template selection
  // 'user' means: use each recipient's languagePreference (default 'no')
  type TplLocaleMode = EmailTemplateLocale | 'user';
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [tplLocale, setTplLocale] = useState<TplLocaleMode>('user');
  // When locale is 'user': only show types available in BOTH 'no' and 'en'
  const tplTypes = useMemo(() => {
    if (tplLocale === 'user') {
      const typeSets = EMAIL_TEMPLATE_LOCALES.map(({ code }) =>
        new Set(templates.filter(template => template.locale === code).map(template => template.type))
      );
      const [firstSet, ...remainingSets] = typeSets;
      return Array.from(firstSet || []).filter(type => remainingSets.every(set => set.has(type)));
    }
    return templates.filter(t => t.locale === tplLocale).map(t => t.type);
  }, [templates, tplLocale]);
  const [tplType, setTplType] = useState<string>('');

  // Computed recipients
  type RecipientRow = {
    id?: string;
    email: string;
    languagePreference?: string;
    status?: string;
    isOnWaitinglist?: boolean;
    notifyFutureEvents?: boolean;
    sendRunningOffers?: boolean;
    editionId?: string;
    // registration fields to support template variables
    unsubscribeToken?: string;
    registrationId?: string;
    userId?: string;
    firstName?: string;
    lastName?: string;
    name?: string;
    registrationNumber?: number | string;
    dateOfBirth?: any;
    nationality?: string;
    phoneCountryCode?: string;
    phoneNumber?: string;
    waitinglistExpires?: any;
    comments?: string;
    termsAccepted?: boolean;
    travelRequired?: string | boolean;
    representing?: string | string[];
    raceDistance?: string;
    paymentMade?: number;
    paymentRequired?: number;
    originalEmail?: string;
    raceStatus?: string;
  };
  const [recipients, setRecipients] = useState<RecipientRow[]>([]);
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const [dryRunOpen, setDryRunOpen] = useState(false);

  // Send test dialog
  const [testOpen, setTestOpen] = useState(false);
  const [testRecipient, setTestRecipient] = useState('');

  // Campaign execution
  const [campaignName, setCampaignName] = useState('');
  const [enqueueing, setEnqueueing] = useState(false);
  const [progress, setProgress] = useState({ total: 0, enqueued: 0 });
  const [batchSize, setBatchSize] = useState(100);
  const [batchDelayMs, setBatchDelayMs] = useState(200);
  const recipientGeneration = useRef(0);
  useEffect(() => {
    recipientGeneration.current += 1;
    setRecipients([]); setSelectedEmails(new Set()); setDryRunOpen(false); setLoading(false);
    return () => { recipientGeneration.current += 1; };
  }, [filterPreset, source, editionId, statuses, excludeCancelled, flagWaitinglist, flagNotifyFuture, flagSendOffers, participation]);

  // Utilities to normalize and format date-like values
  const toDateOrNull = (v: any): Date | null => {
    try {
      if (!v) return null;
      if (v instanceof Date) return v;
      if (typeof v?.toDate === 'function') return v.toDate();
      if (typeof v === 'object' && typeof v.seconds === 'number') {
        // Firestore Timestamp-like
        return new Date(v.seconds * 1000);
      }
      if (typeof v === 'number') {
        // milliseconds epoch or seconds epoch
        return new Date(v > 1e12 ? v : v * 1000);
      }
      const parsed = new Date(v);
      return isNaN(parsed.getTime()) ? null : parsed;
    } catch {
      return null;
    }
  };
  const fmtDate = (d: Date | null, locale: string): string | null =>
    d ? d.toLocaleDateString(locale || undefined, { year: 'numeric', month: '2-digit', day: '2-digit' }) : null;
  const fmtDateTime = (d: Date | null, locale: string): string | null =>
    d ? d.toLocaleString(locale || undefined, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : null;

  useEffect(() => {
    registerDefaultEmailHelpers(Handlebars);
    listEmailTemplates().then(setTemplates).catch(console.error);
    listEventEditions().then(setEditionOptions).catch(console.error);
  }, []);

  // Initialize editionId from context
  useEffect(() => {
    if (event?.id) setEditionId(event.id);
  }, [event?.id]);

  const [editionOptions, setEditionOptions] = useState<EventEditionSummary[]>([]);
  const [editionCache, setEditionCache] = useState<Record<string, { eventName?: string; eventShortName?: string; edition?: number; startTime?: any }>>({});

  const fetchRecipients = async () => {
    const requestGeneration = ++recipientGeneration.current;
    setRecipients([]); setSelectedEmails(new Set()); setDryRunOpen(false);
    setLoading(true);
    try {
      if (needsRaceResults && !/^kutc-\d{4}$/.test(editionId)) throw new Error('Velg én KUTC-årsutgave for løpsstatusfilteret.');
      const rows: RecipientRow[] = [];
      const emailSet = new Set<string>();
      
      // Shared helper for event-series opted-in participant presets
      const fetchPreviousParticipants = async (resultsCollection: string, editionPrefix: string) => {
        // Step 1: Build personId -> user map from users collection
        const usersSnap = await getDocs(collection(db, 'users'));
        const personIdToUser = new Map<number, any>();
        const userIdToUser = new Map<string, any>();
        usersSnap.forEach(d => {
          const u: any = d.data();
          if (u.personId) personIdToUser.set(Number(u.personId), { ...u, docId: d.id });
          if (u.uid) userIdToUser.set(String(u.uid), { ...u, docId: d.id });
        });

        const addUserRow = (u: any, edId?: string) => {
          const notifyOk = u.notifyFutureEvents !== false;
          const offersOk = u.sendRunningOffers !== false;
          if (!notifyOk && !offersOk) return;
          const email = u.email;
          if (!email) return;
          const key = String(email).toLowerCase();
          if (emailSet.has(key)) return;
          emailSet.add(key);
          rows.push({
            email: key,
            languagePreference: u.languagePreference || 'no',
            notifyFutureEvents: !!u.notifyFutureEvents,
            sendRunningOffers: !!u.sendRunningOffers,
            unsubscribeToken: u.unsubscribe_token,
            editionId: edId,
            userId: u.uid,
            firstName: u.firstName,
            lastName: u.lastName,
            name: u.displayName || `${u.firstName || ''} ${u.lastName || ''}`.trim(),
            dateOfBirth: u.dateOfBirth,
            nationality: u.nationality,
            phoneCountryCode: u.phoneCountryCode,
            phoneNumber: u.phone,
            representing: u.representing,
          });
        };

        // Step 2: Query results collection for ALL historical participants
        const editionsSnap = await getDocs(collection(db, resultsCollection));
        for (const editionDoc of editionsSnap.docs) {
          const edId = editionDoc.id;
          const totalResultsRef = collection(db, `${resultsCollection}/${edId}/races/total/results`);
          const totalResultsSnap = await getDocs(totalResultsRef);
          totalResultsSnap.forEach(resultDoc => {
            const personId = Number(resultDoc.id);
            const result: any = resultDoc.data();
            if (String(result.status || '').toUpperCase() === 'DNS') return;
            const user = personIdToUser.get(personId);
            if (user) addUserRow(user, edId);
          });
        }

        // Step 3: Also include matching registrations (catches participants without personId match)
        const allRegsSnap = await getDocs(collection(db, 'registrations'));
        allRegsSnap.forEach(d => {
          const r: any = d.data();
          const regEdId = String(r.editionId || '').toLowerCase();
          if (!regEdId.startsWith(editionPrefix)) return;
          if (String(r.status) === 'cancelled') return;
          const user = r.userId ? userIdToUser.get(r.userId) : null;
          const notifyOk = user ? user.notifyFutureEvents !== false : r.notifyFutureEvents !== false;
          const offersOk = user ? user.sendRunningOffers !== false : r.sendRunningOffers !== false;
          if (!notifyOk && !offersOk) return;
          const email = r.originalEmail || r.email;
          if (!email) return;
          const key = String(email).toLowerCase();
          if (emailSet.has(key)) return;
          emailSet.add(key);
          rows.push({
            id: d.id,
            email: key,
            languagePreference: user?.languagePreference || 'no',
            status: r.status,
            isOnWaitinglist: !!r.isOnWaitinglist,
            notifyFutureEvents: !!(user?.notifyFutureEvents ?? r.notifyFutureEvents),
            sendRunningOffers: !!(user?.sendRunningOffers ?? r.sendRunningOffers),
            editionId: r.editionId,
            registrationId: d.id,
            userId: r.userId,
            firstName: r.firstName,
            lastName: r.lastName,
            name: r.name || `${r.firstName || ''} ${r.lastName || ''}`.trim(),
            registrationNumber: r.registrationNumber,
            dateOfBirth: r.dateOfBirth,
            nationality: r.nationality,
            phoneCountryCode: r.phoneCountryCode,
            phoneNumber: r.phoneNumber,
            representing: r.representing,
            raceDistance: r.raceDistance,
            paymentMade: r.paymentMade,
            paymentRequired: r.paymentRequired,
            originalEmail: r.originalEmail,
          });
        });
      };

      if (filterPreset === 'kutc-opted-in-participants') {
        await fetchPreviousParticipants('kutcResults', 'kutc-');
      } else if (filterPreset === 'mo-opted-in-participants') {
        // moResults is a flat collection: each doc has personId + editionId fields
        // Build users maps first (same as fetchPreviousParticipants step 1)
        const usersSnapMo = await getDocs(collection(db, 'users'));
        const personIdToUserMo = new Map<number, any>();
        const userIdToUserMo = new Map<string, any>();
        usersSnapMo.forEach(d => {
          const u: any = d.data();
          if (u.personId) personIdToUserMo.set(Number(u.personId), { ...u, docId: d.id });
          if (u.uid) userIdToUserMo.set(String(u.uid), { ...u, docId: d.id });
        });
        const addUserRowMo = (u: any, edId?: string) => {
          const notifyOk = u.notifyFutureEvents !== false;
          const offersOk = u.sendRunningOffers !== false;
          if (!notifyOk && !offersOk) return;
          const email = u.email;
          if (!email) return;
          const key = String(email).toLowerCase();
          if (emailSet.has(key)) return;
          emailSet.add(key);
          rows.push({
            email: key,
            languagePreference: u.languagePreference || 'no',
            notifyFutureEvents: !!u.notifyFutureEvents,
            sendRunningOffers: !!u.sendRunningOffers,
            unsubscribeToken: u.unsubscribe_token,
            editionId: edId,
            userId: u.uid,
            firstName: u.firstName,
            lastName: u.lastName,
            name: u.displayName || `${u.firstName || ''} ${u.lastName || ''}`.trim(),
            dateOfBirth: u.dateOfBirth,
            nationality: u.nationality,
            phoneCountryCode: u.phoneCountryCode,
            phoneNumber: u.phone,
            representing: u.representing,
          });
        };
        // Step 2: Scan flat moResults collection — each doc has personId + editionId
        const moResultsSnap = await getDocs(collection(db, 'moResults'));
        moResultsSnap.forEach(resultDoc => {
          const r: any = resultDoc.data();
          if (String(r.status || '').toUpperCase() === 'DNS') return;
          const personId = Number(r.personId);
          if (!Number.isFinite(personId)) return;
          const user = personIdToUserMo.get(personId);
          if (user) addUserRowMo(user, r.editionId);
        });
        // Step 3: Also include MO registrations (catches participants without personId match)
        const allRegsSnapMo = await getDocs(collection(db, 'registrations'));
        allRegsSnapMo.forEach(d => {
          const r: any = d.data();
          const regEdId = String(r.editionId || '').toLowerCase();
          if (!regEdId.startsWith('mo-')) return;
          if (String(r.status) === 'cancelled') return;
          const user = r.userId ? userIdToUserMo.get(r.userId) : null;
          const notifyOk = user ? user.notifyFutureEvents !== false : r.notifyFutureEvents !== false;
          const offersOk = user ? user.sendRunningOffers !== false : r.sendRunningOffers !== false;
          if (!notifyOk && !offersOk) return;
          const email = r.originalEmail || r.email;
          if (!email) return;
          const key = String(email).toLowerCase();
          if (emailSet.has(key)) return;
          emailSet.add(key);
          rows.push({
            id: d.id,
            email: key,
            languagePreference: user?.languagePreference || 'no',
            status: r.status,
            isOnWaitinglist: !!r.isOnWaitinglist,
            notifyFutureEvents: !!(user?.notifyFutureEvents ?? r.notifyFutureEvents),
            sendRunningOffers: !!(user?.sendRunningOffers ?? r.sendRunningOffers),
            editionId: r.editionId,
            registrationId: d.id,
            userId: r.userId,
            firstName: r.firstName,
            lastName: r.lastName,
            name: r.name || `${r.firstName || ''} ${r.lastName || ''}`.trim(),
            registrationNumber: r.registrationNumber,
            dateOfBirth: r.dateOfBirth,
            nationality: r.nationality,
            phoneCountryCode: r.phoneCountryCode,
            phoneNumber: r.phoneNumber,
            representing: r.representing,
            raceDistance: r.raceDistance,
            paymentMade: r.paymentMade,
            paymentRequired: r.paymentRequired,
            originalEmail: r.originalEmail,
          });
        });
      } else if (source === 'registrations' || startedOnly) {
        // Load users first — consent must always come from the user doc, not the registration
        const usersSnapReg = await getDocs(collection(db, 'users'));
        const userByUid = new Map<string, any>();
        const userByDocumentId = new Map<string, any>();
        const userByEmail = new Map<string, any>();
        usersSnapReg.forEach(d => {
          const u: any = d.data();
          userByDocumentId.set(d.id, u);
          userByUid.set(d.id, u);
          if (u.uid) userByUid.set(String(u.uid), u);
          const em = String(u.email || '').toLowerCase();
          if (em) userByEmail.set(em, u);
        });

        let q: any = collection(db, 'registrations');
        const clauses: any[] = [];
        if (editionId) clauses.push(where('editionId', '==', editionId));
        // status filter: allow multiple values
        if (!startedOnly && statuses.length === 1) {
          clauses.push(where('status', '==', statuses[0]));
        }
        // If multiple statuses, we'll filter in code after fetching (to avoid composite constraints).
        if (clauses.length) {
          q = query(q, ...clauses);
        }
        const snap = await getDocs(q);
        const starters = needsRaceResults ? kutcStarterIndex((await getDocs(collection(db, 'kutcResults', editionId, 'races', 'total', 'results'))).docs.map(d => ({ ...d.data(), id: d.id }))) : null;
        snap.forEach(d => {
          const r: any = d.data();
          // In-code filters
          if (!startedOnly && statuses.length > 1 && r.status && !statuses.includes(String(r.status))) return;
          if (!startedOnly && excludeCancelled && String(r.status) === 'cancelled') return;
          if (!startedOnly && flagWaitinglist !== 'any' && Boolean(r.isOnWaitinglist) !== flagWaitinglist) return;

          // Resolve authoritative consent from user doc; fall back to registration for unlinked records
          const regEmail = String(r.originalEmail || r.email || '').toLowerCase();
          const user = (r.userId ? userByUid.get(String(r.userId)) : null) ?? userByEmail.get(regEmail);
          // Email may resolve consent, but it must never resolve the runner's identity.
          const linkedProfile = r.userId ? userByDocumentId.get(String(r.userId)) ?? userByUid.get(String(r.userId)) : undefined;
          const raceStatus = starters && effectiveParticipation !== 'any' ? kutcParticipationStatus(editionId, r, linkedProfile, starters, effectiveParticipation) : null;
          if (starters && !raceStatus) return;
          const notifyConsent = user !== undefined ? user.notifyFutureEvents : r.notifyFutureEvents;
          const offersConsent = user !== undefined ? user.sendRunningOffers : r.sendRunningOffers;

          if (!startedOnly && flagNotifyFuture !== 'any' && Boolean(notifyConsent) !== flagNotifyFuture) return;
          if (!startedOnly && flagSendOffers !== 'any' && Boolean(offersConsent) !== flagSendOffers) return;

          const email = r.originalEmail || r.email;
          if (email) {
            const key = String(email).toLowerCase();
            if (!emailSet.has(key)) {
              emailSet.add(key);
              rows.push({
                id: d.id,
                email: key,
                languagePreference: user?.languagePreference || r.languagePreference || 'no',
                status: r.status,
                ...(raceStatus && { raceStatus }),
                isOnWaitinglist: !!r.isOnWaitinglist,
                notifyFutureEvents: !!notifyConsent,
                sendRunningOffers: !!offersConsent,
                unsubscribeToken: user?.unsubscribe_token,
                editionId: r.editionId,
                registrationId: d.id,
                userId: r.userId,
                firstName: r.firstName,
                lastName: r.lastName,
                name: r.name || `${r.firstName || ''} ${r.lastName || ''}`.trim(),
                registrationNumber: r.registrationNumber,
                dateOfBirth: r.dateOfBirth,
                nationality: r.nationality,
                phoneCountryCode: r.phoneCountryCode,
                phoneNumber: r.phoneNumber,
                waitinglistExpires: r.waitinglistExpires,
                comments: r.comments,
                termsAccepted: r.termsAccepted,
                travelRequired: r.travelRequired,
                representing: r.representing,
                raceDistance: r.raceDistance,
                paymentMade: r.paymentMade,
                paymentRequired: r.paymentRequired,
                originalEmail: r.originalEmail,
              });
            }
          }
        });
      } else {
        // users source (simple initial version: include all users)
        let q: any = collection(db, 'users');
        const snap = await getDocs(q);
        snap.forEach(d => {
          const u: any = d.data();
          if (u.email) {
            const key = String(u.email).toLowerCase();
            if (!emailSet.has(key)) {
              emailSet.add(key);
              rows.push({
                email: key,
                languagePreference: u.languagePreference || 'no',
                unsubscribeToken: u.unsubscribe_token,
                userId: u.uid,
              });
            }
          }
        });
      }

      // Enrich rows missing unsubscribeToken for non-transactional campaigns
      if (campaignType !== 'transactional' && rows.some(r => r.userId && !r.unsubscribeToken)) {
        const usersSnap = await getDocs(collection(db, 'users'));
        const tokenMap = new Map<string, string>();
        usersSnap.forEach(d => {
          const u: any = d.data();
          if (u.uid && u.unsubscribe_token) tokenMap.set(String(u.uid), String(u.unsubscribe_token));
        });
        rows.forEach(r => { if (r.userId && !r.unsubscribeToken) r.unsubscribeToken = tokenMap.get(r.userId); });
      }
      if (recipientGeneration.current !== requestGeneration) return;
      setRecipients(rows);
      setSelectedEmails(new Set(rows.map(r => r.email)));
      setDryRunOpen(true);
    } catch (e) {
      if (recipientGeneration.current === requestGeneration) alert('Failed to fetch recipients: ' + ((e as any)?.message || e));
    } finally {
      if (recipientGeneration.current === requestGeneration) setLoading(false);
    }
  };

  // For 'user' locale mode, preview uses 'no' (default language) template
  const currentTemplate = useMemo(() => {
    const previewLocale = tplLocale === 'user' ? EMAIL_TEMPLATE_LOCALES[0].code : tplLocale;
    return templates.find(t => t.locale === previewLocale && t.type === tplType) || null;
  }, [templates, tplLocale, tplType]);
  const templateValidationIssues = useMemo(() => {
    if (!tplType) return [];
    const requiredLocales = tplLocale === 'user'
      ? EMAIL_TEMPLATE_LOCALES
      : EMAIL_TEMPLATE_LOCALES.filter(({ code }) => code === tplLocale);

    return requiredLocales.flatMap(({ code, label }) => {
      const template = templates.find(t => t.locale === code && t.type === tplType);
      if (!template) return [`${label} (${code}): template is missing.`];

      const issues: string[] = [];
      if (!template.subjectTemplate?.trim()) issues.push(`${label} (${code}): subject is empty.`);
      if (!template.bodyTemplate?.trim()) issues.push(`${label} (${code}): body is empty.`);
      return issues;
    });
  }, [templates, tplLocale, tplType]);
  const recipientsByEmail = useMemo(() => {
    const m = new Map<string, RecipientRow>();
    recipients.forEach(r => m.set(r.email, r));
    return m;
  }, [recipients]);

  const getEditionInfo = async (id?: string) => {
    if (!id) return {} as any;
    if (editionCache[id]) return editionCache[id];
    // Lazy load full edition doc using existing service
    try {
      const mod = await import('../../services/eventEditionService');
      const ed = await mod.getEventEdition(id);
      const info = {
        eventName: ed.eventName,
        eventShortName: ed.eventShortName,
        edition: ed.edition,
        startTime: ed.startTime,
      };
      setEditionCache(prev => ({ ...prev, [id]: info }));
      return info;
    } catch {
      return {} as any;
    }
  };

  const renderTemplate = async (email: string) => {
    if (!currentTemplate) return { subject: '', html: '' };
    const row = recipientsByEmail.get(email);

    // Resolve effective locale: for 'user' mode, use the recipient's stored preference (default 'no')
    const preferredLocale = row?.languagePreference || EMAIL_TEMPLATE_LOCALES[0].code;
    const effectiveLocale = tplLocale === 'user'
      ? (EMAIL_TEMPLATE_LOCALES.some(({ code }) => code === preferredLocale)
        ? preferredLocale
        : EMAIL_TEMPLATE_LOCALES[0].code)
      : tplLocale;
    // Pick the right template for this recipient's locale
    const templateToUse = tplLocale === 'user'
      ? (templates.find(t => t.locale === effectiveLocale && t.type === tplType) || currentTemplate)
      : currentTemplate;
    if (!templateToUse) return { subject: '', html: '' };

    // Build a registration-like object for buildEmailContext (same as real email system)
    const registrationLike: any = {
      id: row?.registrationId,
      email,
      firstName: row?.firstName,
      lastName: row?.lastName,
      name: row?.name,
      registrationNumber: row?.registrationNumber,
      dateOfBirth: row?.dateOfBirth,
      nationality: row?.nationality,
      phoneCountryCode: row?.phoneCountryCode,
      phoneNumber: row?.phoneNumber,
      waitinglistExpires: row?.waitinglistExpires,
      comments: row?.comments,
      termsAccepted: row?.termsAccepted,
      travelRequired: row?.travelRequired,
      representing: row?.representing,
      raceDistance: row?.raceDistance,
      paymentMade: row?.paymentMade,
      paymentRequired: row?.paymentRequired,
      originalEmail: row?.originalEmail,
      isOnWaitinglist: row?.isOnWaitinglist,
      status: row?.status,
      userId: row?.userId,
      editionId: templateEditionOverride || row?.editionId || editionId || event?.id,
      languagePreference: effectiveLocale,
    };

    // Use buildEmailContext to get all canonical variables (logo, URLs, dates, etc.)
    const builtCtx = await buildEmailContext(registrationLike);

    // Supplement with any extra fields not covered by buildEmailContext
    const edInfo = row?.editionId ? (editionCache[row.editionId] || await getEditionInfo(row.editionId)) : {};
    const eventStartDate = toDateOrNull(edInfo?.startTime || event?.startTime);
    const eventDateText = fmtDateTime(eventStartDate, effectiveLocale) || undefined;

    const unsubscribeUrl = (campaignType !== 'transactional' && row?.unsubscribeToken)
      ? `${FUNCTIONS_BASE}/${UNSUBSCRIBE_FUNCTION}?token=${row.unsubscribeToken}&type=${campaignType}&lang=${effectiveLocale}`
      : '';

    const ctx: any = {
      ...builtCtx,
      // Extra fields not in EmailContext
      eventDate: eventDateText,
      eventDateText,
      eventDateRaw: edInfo?.startTime || event?.startTime,
      notifyFutureEvents: row?.notifyFutureEvents,
      sendRunningOffers: row?.sendRunningOffers,
      unsubscribeUrl,
      locale: effectiveLocale,
    };

    // Resolve boilerplates ({{> partialName}}) from Firestore before Handlebars compilation
    const [resolvedSubject, resolvedBody] = await Promise.all([
      resolveBoilerplates(templateToUse.subjectTemplate, { locale: effectiveLocale, onMissingBoilerplate: 'warn' }),
      resolveBoilerplates(templateToUse.bodyTemplate, { locale: effectiveLocale, onMissingBoilerplate: 'warn' }),
    ]);
    const subject = Handlebars.compile(resolvedSubject.resolvedContent)(ctx);
    const html = Handlebars.compile(resolvedBody.resolvedContent)(ctx);
    return { subject, html };
  };

  const startCampaign = async () => {
    if (templateValidationIssues.length > 0) {
      alert(`Campaign not started. Complete the selected template first:\n\n${templateValidationIssues.join('\n')}`);
      return;
    }
    if (!currentTemplate) {
      alert('Select a template first.');
      return;
    }
    const selectedList = Array.from(selectedEmails);
    if (!selectedList.length) {
      alert('No recipients. Run Dry-run to load recipients.');
      return;
    }
    const name = campaignName.trim() || `${tplType} ${new Date().toISOString()}`;
    try {
      setEnqueueing(true);
      setProgress({ total: selectedList.length, enqueued: 0 });
      // Create campaign doc
      const campRef = await addDoc(collection(db, 'campaigns'), {
        name,
        createdAt: serverTimestamp(),
        createdBy: 'admin', // TODO: replace with auth user
        filters: {
          filterPreset,
          source: startedOnly ? 'registrations' : source, editionId,
          statuses: startedOnly ? [] : statuses, excludeCancelled: startedOnly ? false : excludeCancelled,
          participation: effectiveParticipation,
          isOnWaitinglist: startedOnly ? 'any' : flagWaitinglist,
          notifyFutureEvents: startedOnly ? 'any' : flagNotifyFuture,
          sendRunningOffers: startedOnly ? 'any' : flagSendOffers,
        },
        template: { type: tplType, locale: tplLocale === 'user' ? 'user (per-recipient)' : tplLocale },
        campaignType,
        counts: { total: selectedList.length, enqueued: 0, sent: 0, error: 0 },
        status: 'RUNNING',
      });
      const campaignId = campRef.id;

      // Enqueue in batches
      for (let i = 0; i < selectedList.length; i += batchSize) {
        const batch = selectedList.slice(i, i + batchSize);
        await Promise.all(batch.map(async (email) => {
          const row = recipientsByEmail.get(email);
          const rowLang = tplLocale === 'user' ? (row?.languagePreference || 'no') : tplLocale;
          const unsubscribeUrl = (campaignType !== 'transactional' && row?.unsubscribeToken)
            ? `${FUNCTIONS_BASE}/${UNSUBSCRIBE_FUNCTION}?token=${row.unsubscribeToken}&type=${campaignType}&lang=${rowLang}`
            : undefined;
          const { subject, html } = await renderTemplate(email);
          await enqueueRawEmail(email, subject, html, {
            type: tplType,
            context: { locale: tplLocale },
            eventEditionId: editionId,
            campaignId,
            campaignType,
            ...(unsubscribeUrl && { unsubscribeUrl }),
          });
        }));
        const enqueued = Math.min(i + batch.length, selectedList.length);
        setProgress({ total: selectedList.length, enqueued });
        // Update campaign progress
        await updateDoc(doc(db, 'campaigns', campaignId), {
          'counts.enqueued': enqueued,
          lastUpdatedAt: serverTimestamp(),
        });
        if (i + batch.length < selectedList.length) {
          await new Promise(res => setTimeout(res, batchDelayMs));
        }
      }
      await updateDoc(doc(db, 'campaigns', campaignId), {
        status: 'COMPLETED',
        completedAt: serverTimestamp(),
      });
      alert('Campaign enqueued.');
    } catch (e: any) {
      alert('Failed to enqueue campaign: ' + (e?.message || e));
    } finally {
      setEnqueueing(false);
    }
  };

  const sendTest = async () => {
    if (!currentTemplate) {
      alert('Select a template first.');
      return;
    }
    if (!testRecipient) return;
    try {
      const { subject, html } = await renderTemplate(testRecipient);
      await enqueueRawEmail(testRecipient, subject, html, {
        type: tplType,
        context: { locale: tplLocale },
        eventEditionId: editionId,
      });
      alert('Test email enqueued.');
      setTestOpen(false);
      setTestRecipient('');
    } catch (e: any) {
      alert('Failed to enqueue test email: ' + (e?.message || e));
    }
  };

  return (
    <Box>
      <Typography variant="h5">Bulk Email</Typography>
      <Typography variant="body2" sx={{ mt: 1 }}>
        Build a recipient segment, select a template, dry-run to see count, then enqueue as a campaign. Emails are delivered by the SMTP agent.
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 2, mt: 2 }}>
        <FormControl fullWidth>
          <InputLabel id="bulk-preset-label">Filter Preset</InputLabel>
          <Select labelId="bulk-preset-label" value={filterPreset} label="Filter Preset" disabled={enqueueing} onChange={e => {
            const preset = e.target.value as FilterPreset;
            setFilterPreset(preset);
            if (preset === 'kutc-started-edition') setSource('registrations');
          }}>
            <MenuItem value="custom">Custom (manual filters)</MenuItem>
            <MenuItem value="kutc-started-edition">Stilte til start – valgt KUTC-årsutgave (Finished + DNF)</MenuItem>
            <MenuItem value="kutc-opted-in-participants">Previous KUTC participants (opted-in)</MenuItem>
            <MenuItem value="mo-opted-in-participants">Previous MO participants (opted-in)</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth disabled={PRESET_DISABLED(filterPreset) || enqueueing}>
          <InputLabel id="bulk-source-label">Source</InputLabel>
          <Select labelId="bulk-source-label" value={source} label="Source" onChange={e => setSource(e.target.value as SourceType)}>
            <MenuItem value="registrations">Registrations</MenuItem>
            <MenuItem value="users">Users</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth disabled={(PRESET_DISABLED(filterPreset) && !startedOnly) || enqueueing}>
          <InputLabel id="bulk-edition-label">Edition</InputLabel>
          <Select labelId="bulk-edition-label" value={editionId} label="Edition" onChange={e => setEditionId(e.target.value)}>
            <MenuItem value="">All editions</MenuItem>
            {editionOptions.map(ed => (
              <MenuItem key={ed.id} value={ed.id}>{`${ed.eventId}-${ed.edition}`}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth disabled={PRESET_DISABLED(filterPreset)}>
          <InputLabel>Status</InputLabel>
          <Select
            multiple
            value={statuses}
            label="Status"
            onChange={e => setStatuses(typeof e.target.value === 'string' ? [e.target.value] : (e.target.value as string[]))}
          >
            {REG_STATUSES.map(s => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth disabled={PRESET_DISABLED(filterPreset) || source !== 'registrations' || enqueueing}>
          <InputLabel id="bulk-participation-label">Løpsstatus (KUTC)</InputLabel>
          <Select labelId="bulk-participation-label" label="Løpsstatus (KUTC)" value={effectiveParticipation} onChange={e => setParticipation(e.target.value as KutcParticipationFilter)}>
            <MenuItem value="any">Alle – ikke filtrer på løpsstatus</MenuItem>
            <MenuItem value="started">Stilte til start (Finished + DNF)</MenuItem>
            <MenuItem value="finished">Kun fullførte (Finished)</MenuItem>
            <MenuItem value="dnf">Kun DNF</MenuItem>
            <MenuItem value="dns">Kun DNS</MenuItem>
          </Select>
        </FormControl>

        <FormControlLabel
          control={<Checkbox checked={excludeCancelled} onChange={e => setExcludeCancelled(e.target.checked)} disabled={PRESET_DISABLED(filterPreset)} />}
          label="Exclude cancelled"
        />

        {/* Flags */}
        <FormControl fullWidth disabled={PRESET_DISABLED(filterPreset)}>
          <InputLabel>On Waiting-list</InputLabel>
          <Select
            value={String(flagWaitinglist)}
            label="On Waiting-list"
            onChange={e => setFlagWaitinglist((e.target.value as string) === 'any' ? 'any' : e.target.value === 'true')}
          >
            <MenuItem value="any">Any</MenuItem>
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth disabled={filterPreset !== 'custom'}>
          <InputLabel>Notify Future Events</InputLabel>
          <Select
            value={String(flagNotifyFuture)}
            label="Notify Future Events"
            onChange={e => setFlagNotifyFuture((e.target.value as string) === 'any' ? 'any' : e.target.value === 'true')}
          >
            <MenuItem value="any">Any</MenuItem>
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth disabled={filterPreset !== 'custom'}>
          <InputLabel>Send Running Offers</InputLabel>
          <Select
            value={String(flagSendOffers)}
            label="Send Running Offers"
            onChange={e => setFlagSendOffers((e.target.value as string) === 'any' ? 'any' : e.target.value === 'true')}
          >
            <MenuItem value="any">Any</MenuItem>
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {startedOnly && <Alert severity={/^kutc-\d{4}$/.test(editionId) ? 'info' : 'warning'} sx={{ mt: 2 }}>
        Velg én KUTC-årsutgave. Mottakerne er løpere med Finished eller DNF i resultatene for den årsutgaven. DNS og påmeldinger uten bekreftet start utelates. Filtrene for påmeldingsstatus og venteliste brukes ikke i dette utvalget.
      </Alert>}
      {filterPreset === 'custom' && needsRaceResults && <Alert severity={/^kutc-\d{4}$/.test(editionId) ? 'info' : 'warning'} sx={{ mt: 2 }}>
        Løpsstatus hentes fra resultatene for én valgt KUTC-årsutgave. Dette filteret kombineres med de øvrige Custom-filtrene. DNS betyr registrert DNS i resultatene; manglende resultat regnes ikke som DNS.
      </Alert>}

      <Alert severity="info" sx={{ mt: 2 }}>
        <strong>About event &amp; edition template variables:</strong> Variables like{' '}
        <code>{'{{eventName}}'}</code>, <code>{'{{dateEventEditionStartTime}}'}</code>,{' '}
        <code>{'{{datetimeEventEditionRegistrationOpens}}'}</code> etc. are filled from{' '}
        <strong>each recipient&apos;s own registration/result edition</strong> — not from the
        Edition filter above. For preset filters (e.g. &quot;Previous KUTC participants&quot;)
        this means the edition they historically participated in.{' '}
        <strong>Set &quot;Template context edition&quot; below</strong> to override this for all
        recipients — use this when promoting a new upcoming event.
      </Alert>

      <Box sx={{ display: 'flex', gap: 2, mt: 1, mb: 1, alignItems: 'center' }}>
        <FormControl sx={{ minWidth: 320 }}>
          <InputLabel>Template context edition (override)</InputLabel>
          <Select
            value={templateEditionOverride}
            label="Template context edition (override)"
            onChange={e => setTemplateEditionOverride(e.target.value)}
          >
            <MenuItem value=""><em>— Use each recipient&apos;s own edition —</em></MenuItem>
            {editionOptions.map(ed => (
              <MenuItem key={ed.id} value={ed.id}>{`${ed.eventId}-${ed.edition}`}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ mt: 2 }}>
        <FormControl sx={{ minWidth: 300 }}>
          <InputLabel>Campaign Type</InputLabel>
          <Select value={campaignType} label="Campaign Type" onChange={e => setCampaignType(e.target.value as CampaignType)}>
            <MenuItem value="transactional">Transactional (no consent check, no unsubscribe link)</MenuItem>
            <MenuItem value="notifyFutureEvents">Event invitations (notifyFutureEvents consent)</MenuItem>
            <MenuItem value="sendRunningOffers">Running offers / newsletters (sendRunningOffers consent)</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 2, mt: 2 }}>
        <FormControl fullWidth>
          <InputLabel>Locale</InputLabel>
          <Select value={tplLocale} label="Locale" onChange={e => { setTplLocale(e.target.value as any); setTplType(''); }}>
            <MenuItem value="user">Follow user preference (default=no)</MenuItem>
            {EMAIL_TEMPLATE_LOCALES.map(locale => (
              <MenuItem key={locale.code} value={locale.code}>{locale.label} ({locale.code})</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>Template Type</InputLabel>
          <Select value={tplType} label="Template Type" onChange={e => setTplType(e.target.value)}>
            {tplTypes.map(t => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {templateValidationIssues.length > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          The campaign cannot start until the selected template is complete:
          <ul style={{ marginTop: 4, marginBottom: 0 }}>
            {templateValidationIssues.map(issue => <li key={issue}>{issue}</li>)}
          </ul>
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, mt: 2, alignItems: 'center' }}>
        <Button variant="outlined" onClick={fetchRecipients} disabled={loading || enqueueing || (needsRaceResults && !/^kutc-\d{4}$/.test(editionId))}>
          {loading ? <><CircularProgress size={16} sx={{ mr: 1 }} /> Loading…</> : 'Select and load recipients'}
        </Button>
        <Button variant="outlined" onClick={() => setTestOpen(true)} disabled={!tplType}>Send Test</Button>
        <TextField label="Campaign Name" value={campaignName} onChange={e => setCampaignName(e.target.value)} sx={{ minWidth: 240 }} />
        <TextField type="number" label="Batch Size" value={batchSize} onChange={e => setBatchSize(Number(e.target.value) || 100)} sx={{ width: 140 }} />
        <TextField type="number" label="Delay (ms)" value={batchDelayMs} onChange={e => setBatchDelayMs(Number(e.target.value) || 200)} sx={{ width: 140 }} />
        <Button variant="contained" onClick={startCampaign} disabled={loading || !recipients.length || !tplType || enqueueing || selectedEmails.size === 0 || templateValidationIssues.length > 0}>
          {enqueueing ? `Enqueueing ${progress.enqueued}/${progress.total}…` : `Start Campaign (${selectedEmails.size} selected)`}
        </Button>
      </Box>

      <Dialog open={dryRunOpen} onClose={() => setDryRunOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Dry-run Recipients ({recipients.length})</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 1 }}>
            <FormControlLabel
              control={<Checkbox checked={selectedEmails.size === recipients.length && recipients.length > 0}
                                 indeterminate={selectedEmails.size > 0 && selectedEmails.size < recipients.length}
                                 onChange={e => {
                                   if (e.target.checked) setSelectedEmails(new Set(recipients.map(r => r.email)));
                                   else setSelectedEmails(new Set());
                                 }} />}
              label={`Select all (${selectedEmails.size}/${recipients.length})`}
            />
          </Box>
          <Box sx={{ maxHeight: 360, overflow: 'auto', border: 1, borderColor: 'grey.300' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: 8 }}>Sel</th>
                  <th style={{ textAlign: 'left', padding: 8 }}>Email</th>
                  <th style={{ textAlign: 'left', padding: 8 }}>Løper</th>
                  <th style={{ textAlign: 'left', padding: 8 }}>Løpsstatus</th>
                  <th style={{ textAlign: 'left', padding: 8 }}>Status</th>
                  <th style={{ textAlign: 'left', padding: 8 }}>Waiting-list</th>
                  <th style={{ textAlign: 'left', padding: 8 }}>Notify</th>
                  <th style={{ textAlign: 'left', padding: 8 }}>Offers</th>
                  <th style={{ textAlign: 'left', padding: 8 }}>Edition</th>
                </tr>
              </thead>
              <tbody>
                {recipients.map((r) => (
                  <tr key={r.email} style={{ borderTop: '1px solid #ddd' }}>
                    <td style={{ padding: 8 }}>
                      <Checkbox size="small" checked={selectedEmails.has(r.email)} onChange={(e) => {
                        setSelectedEmails(prev => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(r.email); else next.delete(r.email);
                          return next;
                        });
                      }} />
                    </td>
                    <td style={{ padding: 8 }}>{r.email}</td>
                    <td style={{ padding: 8 }}>{r.name || '-'}</td>
                    <td style={{ padding: 8 }}>{r.raceStatus || '-'}</td>
                    <td style={{ padding: 8 }}>{r.status || '-'}</td>
                    <td style={{ padding: 8 }}>{r.isOnWaitinglist ? 'Yes' : 'No'}</td>
                    <td style={{ padding: 8 }}>{r.notifyFutureEvents ? 'Yes' : 'No'}</td>
                    <td style={{ padding: 8 }}>{r.sendRunningOffers ? 'Yes' : 'No'}</td>
                    <td style={{ padding: 8 }}>{r.editionId || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDryRunOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={testOpen} onClose={() => setTestOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Test Email</DialogTitle>
        <DialogContent>
          <TextField
            label="Recipient Email"
            type="email"
            fullWidth
            value={testRecipient}
            onChange={e => setTestRecipient(e.target.value)}
            placeholder="you@example.com"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={sendTest} disabled={!testRecipient || !tplType}>Send</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BulkEmailPanel;
